/** Focus trap + Escape handling for modal dialogs (WCAG 2.1.2, 2.4.3). */

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

export type ModalFocusTrapOptions = {
  onEscape?: () => void;
  initialFocus?: HTMLElement | null;
};

export type ModalFocusTrapHandle = {
  activate: () => void;
  deactivate: () => void;
};

export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el): el is HTMLElement => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.hasAttribute("disabled") || el.tabIndex === -1) return false;
      if (el.getAttribute("aria-hidden") === "true") return false;
      const style = window.getComputedStyle(el);
      return style.visibility !== "hidden" && style.display !== "none";
    },
  );
}

export function createModalFocusTrap(
  container: HTMLElement,
  options: ModalFocusTrapOptions = {},
): ModalFocusTrapHandle {
  let previouslyFocused: HTMLElement | null = null;

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      options.onEscape?.();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = getFocusableElements(container);
    if (!focusable.length) {
      event.preventDefault();
      container.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return {
    activate() {
      previouslyFocused = document.activeElement as HTMLElement | null;
      container.addEventListener("keydown", handleKeydown);

      const focusable = getFocusableElements(container);
      const target =
        options.initialFocus ??
        focusable.find((el) => el.classList.contains("btn-close")) ??
        focusable[0] ??
        container;

      requestAnimationFrame(() => {
        if (target instanceof HTMLElement) {
          target.focus();
        }
      });
    },
    deactivate() {
      container.removeEventListener("keydown", handleKeydown);
      previouslyFocused?.focus?.();
      previouslyFocused = null;
    },
  };
}
